import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	CardAccessComponent,
	INavHelp,
	ITagImageStatus,
	LinkComponent,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
	QrComponent,
	SpinnerComponent,
	TileComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { firstValueFrom, Subject, Subscription, take, takeUntil } from 'rxjs';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { ModalChangeDeviceComponent } from 'src/app/shared/components/modal-change-device/modal-change-device.component';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { ModalHelpNoCameraPermissionsComponent } from 'src/app/shared/components/modal-help-no-camera-permissions/modal-help-no-camera-permissions.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { IMaster } from 'src/app/shared/models/master.models';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import {
	onCameraPreviewSwitchCompleted,
	onCameraPreviewSwitchStarted,
} from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { FullscreenService } from 'src/app/shared/services/fullscreen.service';
import { IdentitySDKService, OperationType } from 'src/app/shared/services/identity-sdk.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { OperationService } from 'src/app/shared/services/step.service';
import { StreamingManagementService } from 'src/app/shared/services/streaming-management.service';
import { TextToSpeechService } from 'src/app/shared/services/text-to-speech.service';
import { environment } from 'src/environments/environment';
import {
	DOCUMENT_CHECK_ID,
	IOcr,
	IOcrErrorResponse,
	IOcrManualConf,
	IOcrManualDocument,
	IOcrManualPayload,
	IOcrResponse,
	OCR_WARNING_TIME,
} from '../../models/ocr';
import {
	OcrLog,
	IAudios,
	IFinishOperation,
	IMessages,
	IOcrOperation,
	IOperation,
	IPageWarning,
	TEST_STATUS,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	quality,
	TestStatus,
} from '../../models/user-identification';
import { OcrService } from '../../services/ocr/ocr.service';
import { resolveDniPhotoSideAsset } from '../../utils/dni-wireframe-photo-side.util';
import { mergeFormConfigFromProcess } from '../../utils/form-operation.util';
import { detectFaceBboxOnJpegDataUrl } from '../../utils/ocr-client-face-bbox.util';
import { PositionDocumentService } from '../../services/ocr/position-document.service';
import { OcrTimerManagerService } from '../../services/ocr/timer-manager.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import { ConfigService } from 'src/app/shared/services/config/config.service';
import {
	getOcrAllowedDniText as resolveOcrAllowedDniText,
	getOcrTutorialDniOnlyTitle,
	isDniForOtherCountriesInOcr,
	isDniForSpainInOcr,
	isSpanishCompany,
	resolveFirstScanOnInitKey,
} from '../../utils/ocr-allowed-dni-label.util';
import {
	isOcrRestrictionErrorCode,
	resolveOcrRestrictionErrorAudioKey,
	resolveOcrRestrictionErrorMessage,
} from '../../utils/ocr-restriction-error.util';
import { KoDebugImagesService } from '../../services/debug/ko-debug-images.service';
import { manualOcrConf, ocrBackConf, ocrFrontConf } from '../ocr/ocr-conf';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';

@Component({
	selector: 'app-manual-ocr',
	imports: [
		TranslatePipe,
		CommonModule,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		CardAccessComponent,
		LinkComponent,
		NavHelpComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		QrComponent,
		SpinnerComponent,
		TileComponent,
		WatermarkComponent,
		VideoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		ModalHelpNoCameraPermissionsComponent,
		ModalChangeDeviceComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './manual-ocr.component.html',
	styleUrls: ['./manual-ocr.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ManualOcrComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() operations: IOperation[];
	@Input()
	get operation(): IOcrOperation {
		return this._operation;
	}
	set operation(value: IOcrOperation) {
		this._operation = value;
		this.showLoading(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_INIT');
		this.manualOcrConf = this.setOcrSetting(value.config);
		this.manualOcrConf.general.startAt = new Date().toISOString();

		// Actualizamos si el pasaporte está permitido
		this.updateIsPassportAllowed();

		// Actualizamos la lista de documentos permitidos
		this.updateAllowedDocumentTypes();

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	private _operation: IOcrOperation;

	// Textos y alertas
	public subtitle: string = '';
	public helpTitle: string = '';
	private sounded: Array<string> = [];

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	public allCameras: Array<MediaDeviceInfo> = [];

	// Configuración de la prueba
	public manualOcrConf: IOcrManualConf;
	private ocrFrontConf: IOcr = JSON.parse(JSON.stringify(ocrFrontConf));
	private ocrBackConf: IOcr = JSON.parse(JSON.stringify(ocrBackConf));
	private subscriptionTimers: Subscription = new Subscription();

	// General
	private initOperation: boolean = false;
	public env = environment;
	public loading: ILoading = { show: false, text: '' };
	public isVisibleScan: boolean = false;
	public isHelpByNavHelp: boolean = false;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public permissionsCamera: boolean = true;
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp();
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	private destroy$: Subject<void> = new Subject<void>();
	private languagePickerUi = inject(LanguagePickerUiService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public PAGE_WARNING = PAGE_WARNING;
	/** Mensaje de restricción OCR (2003/2070/2071) mostrado en ayuda. */
	public restrictionErrorMsg: string = '';
	public handsetLandscape: boolean = false;
	public OcrLog: typeof OcrLog = OcrLog;
	public LogType: typeof LogType = LogType;
	public openQR: boolean = false;
	public documentCheckId = DOCUMENT_CHECK_ID;
	public forceFlipHorizontal: boolean = true; // Forzar giro para poder leer bien los textos
	private isPassportAllowed: boolean = true;

	// Tipo de documento seleccionado
	public selectedDocumentType?: 'dni' | 'nie' | 'passport';
	public selectedDniScope?: 'european' | 'spain';
	private countDocumentTypePhotos: number = 0;

	// Lista de tipos de documentos permitidos
	public allowedDocumentTypes: {
		type: 'dni' | 'nie' | 'passport';
		dniScope?: 'european' | 'spain';
		title: string;
	}[] = [];

	// Imagen capturada
	public capturedImage: string | null = null;
	public imageData16x9: string = '';

	// Conjunto para rastrear los lados enviados exitosamente
	private sentSides: Set<'front' | 'back'> = new Set();

	constructor(
		public progressService: ProgressService,
		public processHelperService: ProcessHelperService,
		public audioService: AudioService,
		public positionDocumentService: PositionDocumentService,
		public cloudLogsService: CloudLogsService,
		public ocrService: OcrService,
		public designService: DesignService,
		public auth: AuthService,
		public timerManagerService: OcrTimerManagerService,
		private loadingService: LoadingService,
		private fullscreenService: FullscreenService,
		private trans: TranslateService,
		private tts: TextToSpeechService,
		private cameraService: CameraService,
		private screenShotService: ScreenshotService,
		private processService: ProcessService,
		private breakpointObserver: BreakpointObserver,
		private navHelpService: NavHelpService,
		private streamingManagementService: StreamingManagementService,
		private errorService: ErrorService,
		private masterService: MasterService,
		private operationService: OperationService,
		private identitySDKService: IdentitySDKService,
		private koDebugImagesService: KoDebugImagesService,
		private configService: ConfigService,
	) {
		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				if (this.handsetLandscape) {
					this.stopTotalTime();
					if (!this.pageWarning.show) {
						this.startWarningTime();
					}
				} else {
					this.startTotalTime();
					if (!this.pageWarning.show) {
						this.stopWarningTime();
					}
				}
			});
	}

	ngOnInit(): void {
		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras;

			const foundIndex = this.navsHelp.findIndex((nav: INavHelp) => nav.id === NavHelpAction.ROTATE);
			if (this.allCameras && this.allCameras.length > 1) {
				if (foundIndex === -1) {
					this.navsHelp = this.navHelpService.getNavsHelpWithCamera();
				}
			} else {
				if (foundIndex > -1) {
					this.navsHelp.splice(foundIndex, 1);
				}
			}
		});
	}

	public getDniPhotoSideAsset(assetPath: string): string {
		return resolveDniPhotoSideAsset(
			assetPath,
			this.operation?.config?.dni_wireframe_photo_side,
			this.designService.isDarkMode(),
		);
	}

	ngAfterViewInit(): void {
		// Asegurar que las cámaras se carguen inmediatamente para que estén disponibles cuando se envíe el resultado
		this.cameraService.getAllCameras();

		// Iniciar operación
		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
		this.checkCameraPermissions();

		// Sin tutorial
		if (!this.operation.config.tutorial) {
			this.audioService.preloadOcrMrzAudios();
		}
		if (window.location.hostname !== 'localhost' && environment.saveScreenShot) {
			this.screenShotService.startSaveScreenshot(OcrLog.SCREENSHOT);
		}
	}

	public nextOperation(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (found) {
			(found as IFinishOperation).config.status = status;
		}
	}

	// #region Main Process
	/*
	 * Service enviar datos
	 */
	public async sendResult(valid: boolean, status: TEST_STATUS = TEST_STATUS.OK): Promise<boolean> {
		this.manualOcrConf.general.finishAt = new Date().toISOString();
		this.showLoading(this.trans.instant('OCR.GENERAL.CHECKING_DATA'));
		const body = this.getBodyResult(valid, status);
		await this.attachFaceBboxIfNeeded(body);

		return new Promise((resolve) => {
			this.ocrService.sendInfoOcr(body).subscribe({
				next: async (response: IOcrResponse | any) => {
					if (response.result === OPERATION_STATUS.OK) {
						// Agregar el lado actual a los enviados
						const currentSide = this.getDocumentSideId();
						this.sentSides.add(currentSide);

						// Resetear audios reproducidos cuando la respuesta es OK
						this.sounded = [];

						// Marcar el documento actual como completo
						const currentDoc = this.manualOcrConf.document.find((ocr: IOcrManualDocument) => !ocr.complete);
						if (currentDoc) currentDoc.complete = true;

						// Notificar al SDK el éxito del OCR manual por lado
						this.notifyDocumentSideComplete(currentSide, response);

						// Actualizar las operations con la response (si procede)
						this.updateOperations(response);

						if (response.required_other_side && response.required_side) {
							// Verificar si el lado requerido ya ha sido enviado
							if (!this.applyRequiredOtherSide(response.required_side)) {
								// El mismo lado se ha enviado dos veces, cancelar el proceso
								this.screenShotService.stopSaveScreenshot();
								this.sendResult(false, TEST_STATUS.KO);
								resolve(false);
								return;
							}
						} else {
							// Notificar al SDK que todo el documento está completo
							this.notifyDocumentComplete(response, currentSide);

							this.screenShotService.stopSaveScreenshot();
							this.nextOperation(OPERATION_STATUS.OK);
						}

						resolve(true);
					} else {
						const error = { error: response as unknown as IOcrErrorResponse };
						this.handleSendResultError(error, resolve);
					}

					this.capturedImage = null;
					this.hideLoading();

					// Actualizamos el subtitle
					this.updateSubtitle();
				},
				error: (error: { error: IOcrErrorResponse }) => {
					this.handleSendResultError(error, resolve);
					this.capturedImage = null;
					this.hideLoading();

					// Actualizamos el subtitle
					this.updateSubtitle();
				},
			});
		});
	}

	private updateOperations(response: IOcrResponse): void {
		// Si no hay process, no hacer nada
		if (!response.process || !response.process?.length) return;

		// Si cambia el número de ops (p. ej. CHOOSE), sustituir el process entero
		if (this.operations?.length !== response.process.length) {
			this.operations = response.process;
			this.operationService.setOperations(this.operations);
			return;
		}

		// NIF-658: misma longitud → fusionar solo FORM (prefill/disabled tras OCR OK).
		const merged = mergeFormConfigFromProcess(this.operations, response.process);
		if (merged === this.operations) {
			return;
		}
		this.operations = merged;
		this.operationService.setOperations(this.operations);
	}

	/**
	 * Maneja los errores que se producen al enviar el resultado del OCR.
	 *
	 * @param error El error recibido de la respuesta del servicio OCR
	 * @param resolve La función resolve de la Promise para devolver el resultado
	 * @returns void
	 */
	private handleSendResultError(error: { error: IOcrErrorResponse }, resolve: (value: boolean) => void): void {
		// Log del error
		this.cloudLogsService.sendCloudLogs({ error }, OcrLog.REQUEST, LogType.Warning);

		// Notificar error al SDK
		const currentSide = this.getDocumentSideId();
		const operationType =
			currentSide === DOCUMENT_CHECK_ID.FRONT ? OperationType.OCR_FRONT : OperationType.OCR_BACK;

		this.identitySDKService.notifyOperationError(operationType, error?.error?.error?.msg || 'OCR Manual Error', {
			documentTestId: currentSide,
			errorCode: error?.error?.error?.error_code,
			documentType: this.getDocumentType(),
		});

		const errorBody = error?.error;
		// KO con required_side: back pide otro lado; copy/audio sin avanzar journey (no es OK + 2ª cara).
		if (errorBody?.required_other_side && errorBody?.required_side) {
			this.promptRequiredSide(errorBody.required_side);
			const retry = errorBody.retry ?? false;
			if (errorBody.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}
			resolve(!retry);
			return;
		}

		// Error general
		if (this.errorService.hasGeneralError({ ...error?.error })) {
			this.screenShotService.stopSaveScreenshot();
			return;
		}

		// Error controlado
		const errorCodeRaw = error?.error?.error?.error_code;
		const errorCodeStr = errorCodeRaw?.toString();
		if (errorCodeStr) {
			console.log('Error controlado');
			const retry = error?.error?.retry ?? false;

			// 2003/2070/2071: mensaje «documento no permitido» (también si retry=false; no tips nitidez).
			if (isOcrRestrictionErrorCode(errorCodeStr)) {
				this.handleInternalCodeError(errorCodeStr as ERROR_CODE, error?.error);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}

			if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
				this.verificationFail(OPERATION_STATUS.KO);
				this.screenShotService.stopSaveScreenshot();
				resolve(false);
				return;
			}

			// NIF-821: retry de recaptura → tips; UX dedicada (2004) se mantiene. 2003 = restricción (NIF-1023).
			const hasDedicatedRetryUi = errorCodeStr === ERROR_CODE.INCORRECT_SIDE;
			if (retry && !hasDedicatedRetryUi) {
				this.showInsufficientOcrReadWarning();
			} else {
				this.handleInternalCodeError(errorCodeStr as ERROR_CODE, error?.error);
			}

			resolve(!retry);
		} else {
			// Error no controlado
			// TODO: estandarizar
			this.setPageWarning(PAGE_WARNING.SERVER_ERROR);
			this.loading.show = false;
			this.screenShotService.stopSaveScreenshot();
			resolve(false);
		}
	}

	/**
	 * Maneja los errores internos relacionados con códigos específicos de error.
	 *
	 * @param errorCode El código de error de tipo `ERROR_CODE` que se ha producido.
	 * @param errorBody Cuerpo de error OCR (país en 2071, etc.).
	 *
	 * Códigos 2003/2070/2071: documento o país no permitido → pantalla «Documento no aceptado» (NIF-1023).
	 *
	 * @see {@link https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2} Documento de referencia de códigos de error.
	 */
	private handleInternalCodeError(errorCode: ERROR_CODE, errorBody?: IOcrErrorResponse): void {
		// https://docs.google.com/document/d/1Ei8S6iVtO9VDZus5y-vde3ZIIOoZIWLL1ctdKkKhp78/edit?pli=1&tab=t.0#heading=h.4w8hp5awjwp2
		if (isOcrRestrictionErrorCode(errorCode)) {
			this.showOcrRestrictionError(errorCode, errorBody);
		} else if (errorCode === ERROR_CODE.INCORRECT_SIDE) {
			this.setPageHelp('valid_docs');
		} else if (errorCode === ERROR_CODE.INSUFFICIENT_OCR_DATA) {
			this.showInsufficientOcrReadWarning();
		}
	}

	/** Muestra la ayuda de documento no aceptado con el mensaje de restricción 2003/2070/2071. */
	private showOcrRestrictionError(errorCode: string, errorBody?: IOcrErrorResponse): void {
		const message = resolveOcrRestrictionErrorMessage(this.trans, errorCode, errorBody);
		this.restrictionErrorMsg = message ?? '';
		this.setPageHelp('invalid_doc');
		if (!message) {
			return;
		}
		// speak() resuelve MP3 por clave i18n; el texto traducido forzaría TTS del navegador.
		const audioKey = resolveOcrRestrictionErrorAudioKey(errorCode);
		this.tts.speak(audioKey ?? message, true);
	}

	private verificationFail(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		this.cloudLogsService.sendCloudLogs(
			{ status: status, observations: PAGE_WARNING.VERIFICATION_FAIL },
			OcrLog.FINISH,
			LogType.Warning,
		);
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status = status;
		} else {
			this.setPageWarning(PAGE_WARNING.VERIFICATION_FAIL);
		}
	}

	/**
	 * Genera el payload para el resultado manual del OCR.
	 *
	 * @private
	 * @param valid - Indica si el resultado es válido.
	 * @param status - El estado de liveness asociado al resultado.
	 * @returns El payload manual del OCR, incluyendo el estado, datos, cámaras, tipo y opcionalmente la imagen.
	 */
	private getBodyResult(valid: boolean, status: TEST_STATUS): IOcrManualPayload {
		const image: string = this.capturedImage;
		// Usar allCamerasValue para obtener el valor actual sincronizadamente
		// Esto asegura que siempre tengamos las cámaras disponibles cuando se envía el resultado
		let camerasToUse: MediaDeviceInfo[] = this.allCameras;
		if (this.cameraService.allCamerasValue && this.cameraService.allCamerasValue.length > 0) {
			camerasToUse = this.cameraService.allCamerasValue;
		}
		const cameras = camerasToUse.map((c: MediaDeviceInfo) => c.label);

		this.koDebugImagesService.sendOcrKoDebugLogs(
			valid,
			status,
			'manual-ocr',
			this.manualOcrConf.document,
			this.capturedImage,
			this.imageData16x9,
			{
				operationType: this.operation?.type,
				documentType: this.getDocumentType(),
			},
		);

		const result: IOcrManualPayload = {
			status: valid,
			data: JSON.stringify({
				status: status,
				ocrConf: {
					...this.manualOcrConf,
					document: this.manualOcrConf.document.map(({ photos, ...rest }) => ({
						...rest,
						photos: photos.map(({ img, ...photoRest }) => photoRest),
					})),
				},
			}),
			cameras,
			device_id: this.auth.getDeviceId(),
			type: this.getDocumentType(),
		};

		// Si tiene image, añadirla
		if (image) result.image = image;

		// Si es valid y no tiene image, añadir string vacío
		if (valid && !image) result.image = '';

		if (this.getDocumentType() === 'PASSPORT') {
			result.is_passport = true;
		}

		return result;
	}

	private async attachFaceBboxIfNeeded(body: IOcrManualPayload): Promise<void> {
		const documentTest = this.manualOcrConf.document.find((ocr: IOcrManualDocument) => !ocr.complete);
		if (
			!body.image ||
			this.operation?.config?.obfuscation_photo !== true ||
			documentTest?.documentTestId === DOCUMENT_CHECK_ID.BACK
		) {
			return;
		}
		const detector = await firstValueFrom(this.processHelperService.getDocumentModel().pipe(take(1)));
		const faceBbox = await detectFaceBboxOnJpegDataUrl(detector, body.image, this.positionDocumentService, 'VIDEO');
		if (faceBbox) {
			body.face_bbox = faceBbox;
		}
	}

	/**
	 * Obtiene el tipo de documento correspondiente basado en el valor de `selectedDocumentType`.
	 *
	 * @returns {'DNI_ESP' | 'TARJ_RESID' | 'PASSPORT' | null} Devuelve 'DNI_ESP' si `selectedDocumentType` es 'dni',
	 * 'TARJ_RESID' si es 'nie', 'PASSPORT' si es 'passport', o `null` si no coincide con ninguno.
	 */
	private getDocumentType(): 'DNI_ESP' | 'TARJ_RESID' | 'PASSPORT' {
		if (this.selectedDocumentType === 'dni') return 'DNI_ESP';
		if (this.selectedDocumentType === 'nie') return 'TARJ_RESID';
		if (this.selectedDocumentType === 'passport') return 'PASSPORT';
		return undefined;
	}

	/**
	 * Notifica al SDK según el lado del documento procesado en modo manual
	 */
	private notifyDocumentSideComplete(documentTestId: DOCUMENT_CHECK_ID, response: IOcrResponse): void {
		if (documentTestId === DOCUMENT_CHECK_ID.FRONT) {
			this.identitySDKService.notifyOperationSuccess(OperationType.OCR_MANUAL_FRONT, {
				documentTestId: DOCUMENT_CHECK_ID.FRONT,
				result: response.result,
				response: response,
				documentType: this.getDocumentType(),
			});
		} else if (documentTestId === DOCUMENT_CHECK_ID.BACK) {
			this.identitySDKService.notifyOperationSuccess(OperationType.OCR_MANUAL_BACK, {
				documentTestId: DOCUMENT_CHECK_ID.BACK,
				result: response.result,
				response: response,
				documentType: this.getDocumentType(),
			});
		}
	}

	/**
	 * Notifica al SDK cuando se completa todo el procesamiento del documento en modo manual
	 */
	private notifyDocumentComplete(response: IOcrResponse, documentTestId: DOCUMENT_CHECK_ID): void {
		this.identitySDKService.notifyOperationSuccess(OperationType.OCR_MANUAL_COMPLETE, {
			documentType: this.getDocumentType(),
			documentTestId,
			result: response.result,
			response: response,
			completed: true,
		});
	}
	// #endregion

	// #region Handle Document
	public getDocumentSideId(): DOCUMENT_CHECK_ID {
		return this.manualOcrConf.document.find((ocr: IOcr) => !ocr.complete)?.documentTestId;
	}

	public takePhoto(): void {
		if (!this.video || !this.canvas || !this.ctx) return;

		const documentTest: IOcrManualDocument | undefined = this.manualOcrConf.document.find(
			(ocr: IOcrManualDocument) => !ocr.complete,
		);
		if (!documentTest) return;

		// Ajustar el canvas al tamaño del video
		this.canvas.width = this.video.videoWidth;
		this.canvas.height = this.video.videoHeight;

		// Calcular el controlRect con las dimensiones del video
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());

		this.drawImageForDocumentValidation();
		if (!this.imageData16x9) return;

		documentTest.photos.push({
			img: this.imageData16x9,
			label: this.cameraService.getCurrentDeviceLabel(),
		});

		this.capturedImage = this.imageData16x9;
		this.updateSubtitle();
	}

	public repeatPhoto(): void {
		const documentTest: IOcrManualDocument = this.manualOcrConf.document.find(
			(ocr: IOcrManualDocument) => !ocr.complete,
		);
		documentTest.photos.pop();

		this.capturedImage = null;
		this.imageData16x9 = '';
		this.updateSubtitle();
	}
	// #endregion

	// #region Tutorial
	public async finishTutorial(): Promise<void> {
		this.operation.config.tutorial = false;
		this.stopWarningTime();
		this.audioService.preloadOcrMrzAudios();

		// Verificar streaming antes de permitir continuar (modo estricto)
		try {
			const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

			if (isRecordingRequired) {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// MODO ESTRICTO: Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ ocr: `Streaming connection failed after tutorial - Aborting: ${streamingError?.toString()}` },
						OcrLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					return;
				}
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{ ocr: `Error checking streaming in finishTutorial: ${error?.toString()}` },
				OcrLog.IN_PROCESS,
				LogType.Error,
			);
		}

		// Fullscreen
		if (!this.operation.config.iframe) {
			this.fullscreenService.enterFullscreen();
		}

		// Actualizamos el subtitle
		this.updateSubtitle();
	}
	// #endregion

	// #region Cámara
	private startWebcam(): void {
		if (this.permissionsCamera && !this.cameraService.hasGetUserMedia()) {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				OcrLog.PRELOAD,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();
		}
	}

	public onCameraError(err: any): void {
		this.hideLoading();
		this.hidePreviewLoading();
		this.manualOcrConf.general.observations.push(`Error: onCameraError - ${err?.toString()}`, OcrLog.PRELOAD);
		if (err?.name === 'NotAllowedError') {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
		} else {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.stopTotalTime();
			this.startWarningTime();
		}
	}

	public onCameraPermissionRevoked(): void {
		this.hideLoading();
		this.hidePreviewLoading();
		this.permissionsCamera = false;
		this.updateSubtitle();
		this.onPermissionsDenied();
	}

	public onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.video = camera.video;
		this.canvas = camera.canvas;
		this.ctx = camera.ctx;
		this.stream = camera.stream;

		if (!this.cameraPermissionsReady) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
		} else {
			this.resetTestClocksAfterPermissionsGranted();
			this.resumeTestTimersIfApplicable();
		}

		this.getAllCameras();

		// Esperar a que el streaming esté completamente activo antes de continuar
		void this.waitForStreamingAndStartProcessing().catch(() => undefined);
	}

	/**
	 * Espera a que el streaming esté activo antes de iniciar el procesamiento de OCR
	 * Si required_recording está activo y el streaming falla, ABORTA la operación (modo estricto)
	 */
	private async waitForStreamingAndStartProcessing(): Promise<void> {
		// Verificar si el streaming está habilitado en la configuración
		const isRecordingRequired = await this.streamingManagementService.isRecordingRequired();

		if (isRecordingRequired) {
			if (this.streamingManagementService.isStreamingRetryInProgress()) {
				await this.streamingManagementService.waitUntilStreamingRetryIdle();
			} else {
				try {
					// Esperar a que el streaming esté listo antes de continuar
					await this.streamingManagementService.waitForStreamingReady();
				} catch (streamingError: any) {
					// Si required_recording está activo, el streaming es OBLIGATORIO
					this.cloudLogsService.sendCloudLogs(
						{ ocr: `Streaming connection failed - Aborting: ${streamingError?.toString()}` },
						OcrLog.IN_PROCESS,
						LogType.Error,
					);

					if (!this.streamingManagementService.isStreamingRetryInProgress()) {
						// Mostrar pantalla de error con botón de reintento
						this.errorService.setStreamingError(async () => {
							await this.streamingManagementService.retryStreaming(() => this.errorService.clearError());
						});
					}
					throw streamingError;
				}
			}
		}

		try {
			await waitForVideoPreviewReady(this.video);

			// Ocultar el loading y permitir que el procesamiento comience
			this.hideLoading();
			this.hidePreviewLoading();

			// Actualizamos el subtitle
			this.updateSubtitle();
		} catch (error: any) {
			// Error crítico no relacionado con streaming
			this.cloudLogsService.sendCloudLogs(
				{ ocr: `Critical error in waitForStreamingAndStartProcessing: ${error?.toString()}` },
				OcrLog.IN_PROCESS,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();

			// Actualizamos el subtitle
			this.updateSubtitle();
		}
	}

	private getAllCameras(): void {
		this.cameraService.getAllCameras();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		this.showLoading(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.cameraService
			.resolveHasCameraPermission(this.ocrService.constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: () => this.finishPermissionCheck(seq, false),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => {
				this.startWebcam();
			}, 500);
		} else {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopActiveGuidanceAudio();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopManualTime();
		this.stopTotalTime();
		this.stopWarningTime();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	private stopActiveGuidanceAudio(): void {
		this.tts.stop();
		this.audioService.stop();
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.resetTestClocksAfterPermissionsGranted();
		this.resumeTestTimersIfApplicable();
	}

	private resetTestClocksAfterPermissionsGranted(): void {
		this.stopManualTime();
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
	}

	private resumeTestTimersIfApplicable(): void {
		if (this.operation?.config?.tutorial && !this.pageWarning.show && !this.handsetLandscape) {
			this.startWarningTime();
		}
		if (!this.handsetLandscape && !this.pageWarning.show) {
			this.startTotalTime();
			this.startManualTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		this.cloudLogsService.sendCloudLogs(
			{
				observations: 'Camera and microphone permissions timeout',
				status: TEST_STATUS.KO_PERMISSION,
			},
			OcrLog.FINISH,
			LogType.Warning,
		);
		void this.sendResult(false, TEST_STATUS.KO_PERMISSION);
	}

	public changeCamera(): void {
		this.cameraService.changeCamera();
	}

	public onCameraSwitchStarted(): void {
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	public onCameraSwitchCompleted(): void {
		onCameraPreviewSwitchCompleted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}
	// #endregion

	private getAudio(data: Partial<{ audios: IAudios; status: TestStatus; messages: IMessages }>): string {
		if (data) {
			return data.audios && data.audios[data.status] ? data.audios[data.status] : data.messages[data.status];
		}
		return '';
	}

	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	/*
	 * Nav Help
	 */
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => this.displayHelpWarning(true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}

	// #region Timer
	public startTest(): void {
		this.startTotalTime();
	}

	private startManualTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startManualTime(() => {
			if (this.timerManagerService.manualTime >= this.manualOcrConf.general.maxTime) {
				this.stopManualTime();
				this.manualOcrConf.general.status = TestStatus.OnFail;
				this.sendResult(false, TEST_STATUS.KO);
			}
		});
	}

	public stopManualTime(): void {
		this.timerManagerService.stopManualTime();
	}

	// Total time
	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startTotalTime();
	}

	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startWarningTime(() => {
			if (this.timerManagerService.warningTime >= OCR_WARNING_TIME) {
				this.stopWarningTime();
				this.manualOcrConf.general.status = TestStatus.OnFail;
				this.sendResult(false, this.getLivenessStatus());
			}
		});
	}
	// #endregion

	private getLivenessStatus(): TEST_STATUS {
		if (this.pageWarning.show) {
			switch (this.pageWarning.type) {
				case PAGE_WARNING.HELP:
					return TEST_STATUS.KO_HELP;
				case PAGE_WARNING.CAMERA_ERROR:
					return TEST_STATUS.KO_CAMERA;
			}
		}
		if (this.handsetLandscape) {
			return TEST_STATUS.KO_HANDSET_LANDSCAPE;
		}
		if (this.operation.config.tutorial) {
			return TEST_STATUS.KO_TUTORIAL;
		}
		return TEST_STATUS.KO;
	}

	public stopWarningTime(): void {
		this.timerManagerService.stopWarningTime();
	}

	private setOcrSetting(ocrConfig: IOcrOperation['config']): IOcrManualConf {
		this.ocrFrontConf.maxTime = ocrConfig?.setting?.side_max_time ?? 150;
		this.ocrBackConf.maxTime = ocrConfig?.setting?.side_max_time ?? 150;
		const confCopy: IOcrManualConf = JSON.parse(JSON.stringify(manualOcrConf));
		const initialDocumentConf: IOcr = JSON.parse(JSON.stringify(this.ocrFrontConf));
		initialDocumentConf.messages.onInit = resolveFirstScanOnInitKey(ocrConfig?.allowed_docs);
		confCopy.document.push(initialDocumentConf);
		return confCopy;
	}

	/** KO: back pide lado concreto; audio, sin avanzar journey. */
	private promptRequiredSide(requiredSide: 'front' | 'back'): void {
		const initKey =
			requiredSide === 'front' ? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT' : 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT';
		this.sounded = [];
		this.tts.speak(initKey);
		this.updateSubtitle();
	}

	/** OK: avanza al siguiente lado indicado por el back. */
	private applyRequiredOtherSide(requiredSide: 'front' | 'back'): boolean {
		// Verificar si el lado requerido ya ha sido enviado
		if (this.sentSides.has(requiredSide)) {
			return false;
		}

		const currentDoc = this.manualOcrConf.document.find((ocr: IOcrManualDocument) => !ocr.complete);
		if (currentDoc) {
			currentDoc.complete = true;
		}

		const nextConf: IOcr = JSON.parse(
			JSON.stringify(requiredSide === 'front' ? this.ocrFrontConf : this.ocrBackConf),
		);
		// Usar la clave de texto ONINIT sin la mención a pasaporte
		nextConf.messages.onInit =
			requiredSide === 'front' ? 'OCR.FRONT.INSTRUCTIONS.TEXT.ONINIT' : 'OCR.BACK.INSTRUCTIONS.TEXT.ONINIT';
		this.manualOcrConf.document.push(nextConf);
		this.timerManagerService.resetTotalTime();
		return true;
	}

	private displayHelpWarning(isHelpByNavHelp: boolean): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		this.setPageHelp();
		this.stopTotalTime();
		this.startWarningTime();
	}

	private showInsufficientOcrReadWarning(): void {
		this.setPageWarning(PAGE_WARNING.INSUFFICIENT_OCR_READ);
		this.stopTotalTime();
		this.startWarningTime();
		this.loading.show = false;
	}

	public retryInsufficientOcrRead(): void {
		this.setPageWarning(null, false);
		this.stopWarningTime();
		this.startTotalTime();
		if (this.capturedImage) {
			this.repeatPhoto();
		}
	}

	/**
	 * Establece la ayuda de la página según el tipo especificado.
	 *
	 * @param type Tipo de ayuda a mostrar: 'anv_rev' para revisión de anverso, 'valid_docs' para documentos válidos. Por defecto es 'anv_rev'.
	 *
	 * Actualiza el título de la ayuda, la visibilidad de la sección de documentos válidos
	 * y muestra la advertencia de ayuda correspondiente.
	 */
	private setPageHelp(type: 'anv_rev' | 'valid_docs' | 'invalid_doc' | 'change_device' = 'anv_rev'): void {
		const isHelpDocs = type !== 'anv_rev' && type !== 'change_device';

		let helpDocTitle = 'OCR.HELP.TITLE_VALID_DOCUMENTS';
		if (type === 'invalid_doc') helpDocTitle = 'OCR.HELP.TITLE_INVALID_DOCUMENT';

		this.helpTitle = isHelpDocs ? helpDocTitle : 'LIFE_CHECK.HELP.TITLE';
		if (type !== 'invalid_doc') {
			this.restrictionErrorMsg = '';
		}
		this.setPageWarning(PAGE_WARNING.HELP);
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia en la página.
	 *
	 * Esta función actualiza el objeto `pageWarning` para mostrar u ocultar una advertencia en la interfaz,
	 * y define el tipo de advertencia que se debe mostrar.
	 *
	 * @param type El tipo de advertencia de la página, puede ser `PAGE_WARNING` o `null`.
	 * @param show Indica si se debe mostrar la advertencia en la página. Por defecto es `true`.
	 */
	public setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning.show = show;
		this.pageWarning.type = type;
		if (!show || type !== PAGE_WARNING.HELP) {
			this.restrictionErrorMsg = '';
		}

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Reproduce un mensaje de texto usando el TextToSpeechService solo si el texto actual ha cambiado.
	 *
	 * @param currentText - El texto actual mostrado.
	 * @param newText - El nuevo texto que se va a mostrar.
	 * @param textToSpeak - El texto que se debe reproducir por voz.
	 */
	private speakIfTextChanged(currentText: string, newText: string, textToSpeak: string): void {
		if (currentText === newText || this.sounded.includes(textToSpeak)) return;
		this.sounded.push(textToSpeak);
		this.tts.speak(textToSpeak);
	}

	/**
	 * Actualiza la propiedad `isPassportAllowed` indicando si el pasaporte está permitido
	 * según la configuración de documentos permitidos en la operación actual.
	 *
	 * - Si `allowed_docs.passport` es un array, se consideran permitidos (un array vacío implica que se permiten todos).
	 * - Si `allowed_docs` no contiene la propiedad `passport`, significa que no se permiten pasaportes.
	 */
	private updateIsPassportAllowed(): void {
		const allowedDocs = this.operation?.config?.allowed_docs;
		const passport = allowedDocs ? allowedDocs?.passport : undefined;

		this.isPassportAllowed = passport && Array.isArray(passport) && passport.length >= 0;
	}

	/**
	 * Actualiza la lista de tipos de documentos permitidos basada en la configuración de la operación.
	 */
	private updateAllowedDocumentTypes(): void {
		this.allowedDocumentTypes = [];
		const companyCountry = this.configService.getCurrentConfig()?.company_country;

		if (isSpanishCompany(companyCountry)) {
			if (isDniForOtherCountriesInOcr(this.operation)) {
				this.allowedDocumentTypes.push({
					type: 'dni',
					dniScope: 'european',
					title: 'OCR.GENERAL.DOCUMENT_TYPE.DNI_EUROPEAN',
				});
			}
			if (isDniForSpainInOcr(this.operation)) {
				this.allowedDocumentTypes.push({
					type: 'dni',
					dniScope: 'spain',
					title: 'DOCUMENTS.TYPES.DNI',
				});
			}
		} else if (this.operation?.config?.allowed_docs?.dni) {
			this.allowedDocumentTypes.push({ type: 'dni', title: 'OCR.GENERAL.DOCUMENT_TYPE.DOCUMENT_ID' });
		}

		if (this.operation?.config?.allowed_docs?.nie) {
			this.allowedDocumentTypes.push({ type: 'nie', title: 'OCR.GENERAL.DOCUMENT_TYPE.NIE' });
		}
		if (this.operation?.config?.allowed_docs?.passport) {
			this.allowedDocumentTypes.push({ type: 'passport', title: 'OCR.GENERAL.DOCUMENT_TYPE.PASSPORT' });
		}
	}

	private getDocumentTypeTitle(type: 'dni' | 'nie' | 'passport', dniScope?: 'european' | 'spain'): string {
		if (type === 'dni') {
			return resolveOcrAllowedDniText(
				this.trans,
				this.operation,
				this.configService.getCurrentConfig()?.company_country,
				dniScope,
			);
		}

		const found = this.allowedDocumentTypes.find((doc) => doc.type === type);
		return found ? this.trans.instant(found.title) : '';
	}

	public getAllowedDocumentTypeTitle(doc: {
		type: 'dni' | 'nie' | 'passport';
		dniScope?: 'european' | 'spain';
		title: string;
	}): string {
		if (doc.type === 'dni') {
			return resolveOcrAllowedDniText(
				this.trans,
				this.operation,
				this.configService.getCurrentConfig()?.company_country,
				doc.dniScope,
			);
		}

		return this.trans.instant(doc.title);
	}

	/**
	 * Obtiene el status del tagImage basado en la posición (índice) en la lista de documentos permitidos.
	 *
	 * @param index El índice en la lista de documentos permitidos.
	 * @returns El status correspondiente: 'notice' para el índice 0, 'control' para el 1, 'default' para el 2 o siguientes.
	 */
	public getTagImageStatusByIndex(index: number): ITagImageStatus {
		const statuses: ITagImageStatus[] = ['notice', 'control', 'default', 'level'];
		return statuses[index] ?? 'default';
	}

	/**
	 * Selecciona el tipo de documento cuando se pulsa en una de las opciones.
	 *
	 * @param type El tipo de documento seleccionado ('dni', 'nie', 'passport').
	 */
	public selectDocumentType(type: 'dni' | 'nie' | 'passport', dniScope?: 'european' | 'spain'): void {
		this.selectedDocumentType = type;
		this.selectedDniScope = dniScope;
		this.countDocumentTypePhotos = this.getCountDocumentTypePhotos();

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Reinicia el tipo de documento seleccionado.
	 */
	public resetDocumentType(): void {
		this.selectedDocumentType = undefined;
		this.selectedDniScope = undefined;
		this.capturedImage = null;

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	/**
	 * Obtiene el número de fotos requeridas para el tipo de documento seleccionado.
	 *
	 * @returns {number} El número de fotos: 0 si no hay tipo seleccionado, 1 para pasaporte, 2 para otros tipos.
	 */
	private getCountDocumentTypePhotos(): number {
		if (this.selectedDocumentType === undefined) return 0;
		if (this.selectedDocumentType === 'passport') return 1;
		return 2;
	}

	/**
	 * Obtiene el número de la foto actual que se está tomando (1 para primera, 2 para segunda, etc.).
	 *
	 * @returns {number} El número de la foto actual.
	 */
	public getCurrentPhotoNumber(): number {
		const currentIndex = this.manualOcrConf.document.findIndex((ocr: IOcrManualDocument) => !ocr.complete);
		return currentIndex + 1;
	}

	/**
	 * Obtiene el número total de fotos requeridas para el documento seleccionado.
	 *
	 * @returns {number} El número total de fotos.
	 */
	private getTotalPhotos(): number {
		return this.manualOcrConf.document.length;
	}

	// #region Gestión de los textos
	/**
	 * Genera el subtítulo adecuado para la interfaz de usuario del componente OCR,
	 * basado en el estado actual de los permisos, advertencias, configuración de la operación,
	 * y el progreso del reconocimiento de documentos.
	 *
	 * @returns {string} El subtítulo traducido correspondiente al estado actual, o una cadena vacía si no aplica.
	 */
	private generateSubtitle(): string {
		if (this.shouldReturnEmptySubtitle()) return '';
		if (this.isHelpMode()) return this.getHelpSubtitle();
		if (this.isTutorialMode()) return this.getTutorialSubtitle();
		if (this.isLoadingOrWarning()) return '';
		return this.getDocumentSelectionSubtitle();
	}

	/**
	 * Determina si el subtítulo debe estar vacío debido a condiciones bloqueantes.
	 *
	 * @returns {boolean} True si no hay permisos de cámara, está en landscape o hay advertencias bloqueantes.
	 */
	private shouldReturnEmptySubtitle(): boolean {
		return !this.permissionsCamera || this.handsetLandscape || this.isBlockingWarning();
	}

	/**
	 * Verifica si hay una advertencia que bloquea la visualización de subtítulos.
	 *
	 * @returns {boolean} True si el tipo de advertencia es bloqueante.
	 */
	private isBlockingWarning(): boolean {
		return [PAGE_WARNING.VERIFICATION_FAIL, PAGE_WARNING.SERVER_ERROR, PAGE_WARNING.CAMERA_ERROR].includes(
			this.pageWarning.type,
		);
	}

	/**
	 * Verifica si la interfaz está en modo ayuda.
	 *
	 * @returns {boolean} True si se está mostrando la ayuda.
	 */
	private isHelpMode(): boolean {
		return this.pageWarning.type === PAGE_WARNING.HELP;
	}

	/**
	 * Obtiene el subtítulo para el modo ayuda.
	 *
	 * @returns {string} El texto traducido de ayuda.
	 */
	private getHelpSubtitle(): string {
		return this.trans.instant('OCR.BACK.HELP.DESCRIPTION');
	}

	/**
	 * Verifica si la interfaz está en modo tutorial.
	 *
	 * @returns {boolean} True si el tutorial está activo.
	 */
	private isTutorialMode(): boolean {
		return this.operation?.config.tutorial === true;
	}

	/**
	 * Obtiene el subtítulo apropiado para el modo tutorial.
	 *
	 * @returns {string} El texto traducido del tutorial según el tipo de operación y documentos permitidos.
	 */
	private getTutorialSubtitle(): string {
		if (!this.isPassportAllowed) {
			return this.operation.type === OPERATION_TYPE.OCR
				? getOcrTutorialDniOnlyTitle(
						this.trans,
						this.operation,
						this.configService.getCurrentConfig()?.company_country,
					)
				: this.trans.instant('OCR.FRONT.TUTORIAL_TITLE');
		}
		return this.operation.type === OPERATION_TYPE.OCR
			? this.trans.instant('OCR.BACK.TUTORIAL_TITLE')
			: this.trans.instant('OCR.FRONT.TUTORIAL_TITLE');
	}

	/**
	 * Verifica si se está mostrando el loading o hay advertencias activas.
	 *
	 * @returns {boolean} True si hay loading, advertencias o landscape activo.
	 */
	private isLoadingOrWarning(): boolean {
		return this.loading.show || this.pageWarning.show || this.handsetLandscape;
	}

	/**
	 * Obtiene el subtítulo para la selección y captura de documentos.
	 *
	 * @returns {string} El texto traducido según el estado de selección y captura del documento.
	 */
	private getDocumentSelectionSubtitle(): string {
		if (this.operation?.config.tutorial) return '';

		if (!this.selectedDocumentType) {
			const message = 'OCR.MANUAL.SELECT_DOCUMENT_TYPE';
			const translatedMessage = this.trans.instant(message);
			this.speakIfTextChanged(this.subtitle, translatedMessage, message);
			return translatedMessage;
		}

		if (this.capturedImage) {
			const message = 'OCR.MANUAL.CHECK_INFO';
			const translatedMessage = this.trans.instant(message);
			this.speakIfTextChanged(this.subtitle, translatedMessage, message);
			return translatedMessage;
		}

		return this.getPhotoInstructionSubtitle();
	}

	/**
	 * Obtiene el subtítulo de instrucciones para tomar la foto del documento.
	 *
	 * @returns {string} El texto traducido con instrucciones específicas según el tipo y lado del documento.
	 */
	private getPhotoInstructionSubtitle(): string {
		const title = this.getDocumentTypeTitle(
			this.selectedDocumentType,
			this.selectedDocumentType === 'dni' ? this.selectedDniScope : undefined,
		);
		const currentSide = this.getDocumentSideId();
		const photoNumber = this.getCurrentPhotoNumber();

		let message: string;
		if (this.countDocumentTypePhotos === 1) {
			message = 'OCR.MANUAL.TAKE_PHOTO_SINGLE';
		} else if (currentSide === 'front') {
			message = photoNumber === 1 ? 'OCR.MANUAL.TAKE_PHOTO_FRONT_FIRST' : 'OCR.MANUAL.TAKE_PHOTO_FRONT_SECOND';
		} else if (currentSide === 'back') {
			message = photoNumber === 1 ? 'OCR.MANUAL.TAKE_PHOTO_BACK_FIRST' : 'OCR.MANUAL.TAKE_PHOTO_BACK_SECOND';
		} else {
			return '';
		}

		const translatedMessage = this.trans.instant(message, { title });
		this.speakIfTextChanged(this.subtitle, translatedMessage, message);
		return translatedMessage;
	}
	// #endregion

	// #region Loading
	/**
	 * Muestra un indicador de carga personalizado según la configuración de la operación.
	 * Si la operación utiliza un iframe, muestra el indicador de carga en el iframe;
	 * de lo contrario, muestra el indicador de carga estándar.
	 *
	 * @param text Texto opcional que se mostrará junto al indicador de carga.
	 */
	private showLoading(text = ''): void {
		this.operation.config.iframe ? this.loadingService.showIframe(text) : this.loadingService.show(text);
	}

	/**
	 * Oculta el indicador de carga personalizado.
	 * Si la configuración de la operación utiliza un iframe, oculta el indicador de carga específico para iframes;
	 * de lo contrario, oculta el indicador de carga estándar.
	 */
	private hideLoading(): void {
		this.operation.config.iframe ? this.loadingService.hideIframe() : this.loadingService.hide();
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		this.loading = { show: false, text: '' };
	}
	// #endregion

	private getAnalysisViewportAspect(): number {
		const previewEl = this.video ?? this.canvas;
		if (!previewEl) return 1;
		const rect = previewEl.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) {
			return rect.width / rect.height;
		}
		if (this.video?.videoWidth && this.video?.videoHeight) {
			return this.video.videoWidth / this.video.videoHeight;
		}
		return 1;
	}

	private drawImageForDocumentValidation(): HTMLCanvasElement | null {
		if (!this.ctx || !this.canvas || !this.video) return null;

		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		if (!vw || !vh) return null;

		if (this.canvas.width !== vw || this.canvas.height !== vh) {
			this.canvas.width = vw;
			this.canvas.height = vh;
		}
		this.positionDocumentService.setControlRect(this.canvas, this.getAnalysisViewportAspect());
		const sourceRect = this.positionDocumentService.getVisibleAnalysisSourceRect();
		if (!sourceRect) return null;

		const { sx, sy, sw, sh } = sourceRect;

		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = Math.max(1, Math.round(sw));
		tempCanvas.height = Math.max(1, Math.round(sh));
		const tempCtx = tempCanvas.getContext('2d');

		if (!tempCtx) return null;

		tempCtx.drawImage(this.video, sx, sy, sw, sh, 0, 0, tempCanvas.width, tempCanvas.height);
		this.imageData16x9 = tempCanvas.toDataURL('image/jpeg', quality);

		this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
		return tempCanvas;
	}

	ngOnDestroy(): void {
		this.cameraPermissionsTimerService.stopWait();
		// Verificar si el streaming está activo para decidir si detener los tracks
		const isStreamingActive = this.streamingManagementService?.isStreamingActive() ?? false;
		// Desuscribirse de todos los timers
		this.video = null;
		// Detener TODOS los timers del servicio
		this.stopTotalTime();
		this.stopWarningTime();
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
		}
		// Solo detener los tracks si NO hay streaming activo
		if (!isStreamingActive && this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
		}
		this.stream = null;
		this.screenShotService.stopSaveScreenshot();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
